import { useEffect, useRef, useState, useCallback } from "react";
import { CARD } from "../lib/planner";
import { Button } from "../components/ui/button";
import { Play, Pause, RotateCcw, Timer, X } from "lucide-react";
import { toast } from "sonner";

const MODES = {
  work: { label: "Focus", mins: 25 },
  short: { label: "Short break", mins: 5 },
  long: { label: "Long break", mins: 15 },
};

const readFocusTask = () => {
  try {
    return JSON.parse(localStorage.getItem("focusTask") || "null");
  } catch {
    return null;
  }
};

export const Pomodoro = () => {
  const [mode, setMode] = useState("work");
  const [secs, setSecs] = useState(MODES.work.mins * 60);
  const [running, setRunning] = useState(false);
  const [completed, setCompleted] = useState(0);
  const [task, setTask] = useState(readFocusTask());
  const intervalRef = useRef(null);

  useEffect(() => {
    const onChange = () => setTask(readFocusTask());
    window.addEventListener("focus-task-changed", onChange);
    return () => window.removeEventListener("focus-task-changed", onChange);
  }, []);

  const switchMode = useCallback((m) => {
    setRunning(false);
    setMode(m);
    setSecs(MODES[m].mins * 60);
  }, []);

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setSecs((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          setRunning(false);
          if (mode === "work") {
            setCompleted((c) => c + 1);
            toast.success("Pomodoro done — take a break 🌿");
          } else {
            toast.success("Break over — ready when you are");
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running, mode]);

  const total = MODES[mode].mins * 60;
  const progress = ((total - secs) / total) * 100;
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");

  const clearTask = () => {
    localStorage.removeItem("focusTask");
    window.dispatchEvent(new Event("focus-task-changed"));
  };

  return (
    <div className="space-y-6" data-testid="pomodoro-page">
      <h2 className="font-heading text-3xl font-bold text-[#3b2a24] dark:text-stone-100">Focus timer</h2>

      {/* Mode switcher */}
      <div className="flex gap-2 bg-stone-100 dark:bg-stone-800 rounded-full p-1">
        {Object.entries(MODES).map(([k, v]) => (
          <button
            key={k}
            data-testid={`pomo-mode-${k}`}
            onClick={() => switchMode(k)}
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${
              mode === k ? "bg-white dark:bg-stone-700 shadow-sm text-[#3b2a24] dark:text-stone-100" : "text-stone-400 dark:text-stone-500"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div className={`${CARD} p-8 text-center`}>
        {task ? (
          <div data-testid="linked-task" className="flex items-center justify-center gap-2 mb-6 text-sm text-stone-500 dark:text-stone-400">
            <Timer className="h-4 w-4" />
            <span className="truncate max-w-[220px]">{task.content}</span>
            <button data-testid="clear-linked-task" onClick={clearTask} className="text-stone-300 hover:text-rose-500">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <p className="mb-6 text-sm text-stone-400">Tap the timer icon on any task to focus on it.</p>
        )}

        {/* Progress ring */}
        <div className="relative mx-auto h-56 w-56">
          <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" className="fill-none stroke-stone-100 dark:stroke-stone-800" strokeWidth="6" />
            <circle
              cx="50" cy="50" r="45"
              className="fill-none stroke-[#b1543b] dark:stroke-stone-200 transition-all duration-1000"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 45}
              strokeDashoffset={2 * Math.PI * 45 * (1 - progress / 100)}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span data-testid="pomo-time" className="font-heading text-5xl font-semibold text-[#3b2a24] dark:text-stone-100">
              {mm}:{ss}
            </span>
            <span className="text-xs uppercase tracking-[0.2em] text-stone-400 mt-1">{MODES[mode].label}</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 mt-8">
          <Button
            data-testid="pomo-toggle"
            onClick={() => setRunning((r) => !r)}
            className="h-14 w-14 rounded-full bg-[#b1543b] hover:bg-[#8f3f2b] dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-white p-0"
          >
            {running ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
          </Button>
          <Button
            data-testid="pomo-reset"
            variant="outline"
            onClick={() => switchMode(mode)}
            className="h-14 w-14 rounded-full p-0 dark:border-stone-700 dark:text-stone-200"
          >
            <RotateCcw className="h-5 w-5" />
          </Button>
        </div>

        <p className="mt-6 text-sm text-stone-400" data-testid="pomo-count">
          {completed} focus session{completed === 1 ? "" : "s"} completed today
        </p>
      </div>
    </div>
  );
};
